import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { UserRound } from 'lucide-react';
import type { SetTeamMembers, TeamMemberList } from '@mnc/contracts';
import { api } from '../../lib/api';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { UserPicker } from './UserPicker';

/** Team members / assignees of a record. Assignment is an implicit visibility grant; added members are notified. */
export function TeamPicker({ ownerType, ownerId, canEdit, title }: { ownerType: string; ownerId: string; canEdit: boolean; title?: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const key = ['team', ownerType, ownerId];
  const team = useQuery({ queryKey: key, queryFn: () => api.get<TeamMemberList>(`/api/team?owner_type=${ownerType}&owner_id=${ownerId}`) });
  const [ids, setIds] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (team.data) {
      setIds(team.data.items.map((m) => m.user_id));
      setDirty(false);
    }
  }, [team.data]);
  const save = useMutation({
    mutationFn: (body: SetTeamMembers) => api.put<TeamMemberList>(`/api/team?owner_type=${ownerType}&owner_id=${ownerId}`, body),
    onSuccess: (data) => {
      qc.setQueryData(key, data);
      setDirty(false);
      toast.success(t('team.saved'));
    },
    onError: (e) => toast.error(describeError(e, t)),
  });

  return (
    <div className="card p-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <UserRound size={16} className="text-neutral-500" />
        {title ?? t('team.title')}
      </div>
      {team.data && team.data.items.length === 0 && !canEdit && <p className="text-sm text-neutral-500">{t('team.empty')}</p>}
      <ul className="mb-3 flex flex-wrap gap-2">
        {(team.data?.items ?? []).map((m) => (
          <li key={m.user_id} className="flex items-center gap-2 rounded-full bg-neutral-100 py-1 pe-3 ps-1 text-sm dark:bg-neutral-800">
            <span className="h-6 w-6 overflow-hidden rounded-full bg-neutral-300 dark:bg-neutral-700">{m.avatar_url && <img src={m.avatar_url} alt="" className="h-full w-full object-cover" />}</span>
            <span dir="auto">{pick(i18n.language, m.display_name_en, m.display_name_ar)}</span>
            {m.role && <span className="text-xs text-neutral-500">· {m.role}</span>}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div className="grid gap-2">
          <UserPicker value={ids} onChange={(v) => { setIds(v); setDirty(true); }} />
          {dirty && (
            <div>
              <button type="button" className="btn-primary" disabled={save.isPending} onClick={() => save.mutate({ members: ids.map((user_id) => ({ user_id, role: team.data?.items.find((m) => m.user_id === user_id)?.role ?? null })) })}>
                {t('app.save')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
